import React, { useEffect, useState, useRef, useMemo } from 'react';

import _ from 'lodash';

import styled from 'styled-components';

import { useProjectContext, useProjectContextDispatch } from '@contexts/ProjectContext';
import { useStateValue } from '@contexts/StateProvider';

import { onGetOrderedEventList } from '@hooks/API/Project/UseProject';

import { Loading } from '@components/Gui/Loading';
import PackageDrawer from '@components/PackageDrawer/PackageDrawer';
import EventCard from '@components/Project/EventCards/EventCard';
import ProjectTimelineEvent from '@components/Project/ProjectEventsTimeline/ProjectTimelineEvent';

const TimeLineContainer = styled.div`
    & > div {
        padding: 0 0 30px 0;
    }
`;

const ProjectEventsTimelineContainer = styled.div`
    white-space: nowrap;
    overflow-x: auto;
    background: #f0f0f0;

    padding: 15px 30px 20px 40px;

    > div {
        margin: 0px 10px;
        display: inline-block;
        box-shadow: 0px 1px 4px rgb(0 0 0 / 30%);
        background: white;
    }
    > div:first-child {
        margin-left: 0px;
    }
    > div:last-child {
        margin-right: 0px;
    }
`;

//onMoveToEvent
const ProjectEvents = ({ modelId, openOnEvent }) => {
    const { projectId, projectData: currentProject, selectedEvent } = useProjectContext();
    const projectDispatch = useProjectContextDispatch();
    const [{ areaContext }] = useStateValue();
    const [selectedEventObject, setSelectedEventObject] = useState(null);
    //const [projectEvents, setProjectEvents] = useState([]);
    const isFetching = false;
    const [packagePreviewID, setPackagePreviewID] = useState(null);

    const getEventID = (event) => {
        return event.ID || event.GroupedEventIDs[0];
    };

    const timelineEvents = useMemo(() => {
        if (!isFetching && currentProject) {
            const te = onGetOrderedEventList(currentProject, areaContext, true, false);
            return te;
        }
        return [];
    }, [currentProject, isFetching]);

    // useEffect(() => {
    //     if (!isFetching && currentProject) {
    //         const te = onGetOrderedEventList(currentProject, areaContext, true, false);
    //         setProjectEvents(te);
    //     }
    // }, [currentProject, isFetching]);

    useEffect(() => {
        setSelectedEventObject(null);
        projectDispatch({ type: 'ClearSelectedEvent' });
    }, [projectId, projectDispatch]);

    // Extract stable primitive values from openOnEvent to avoid infinite loop
    const openOnEventID = openOnEvent?.ID;
    const openOnEventTypeName = openOnEvent?.TypeName;

    useEffect(() => {
        if (openOnEventID) {
            projectDispatch({ type: 'SetSelectedEvent', eventID: openOnEventID, eventType: openOnEventTypeName });
        } else {
            setSelectedEventObject(null);
        }
    }, [openOnEventID, openOnEventTypeName, projectDispatch]);

    // Extract stable primitive values from selectedEvent to avoid infinite loop
    const selectedEventID = selectedEvent?.ID;
    const selectedEventTypeName = selectedEvent?.TypeName;

    useEffect(() => {
        if (timelineEvents.length) {
            if (!selectedEventID) {
                const firstevent = timelineEvents[0];
                const idfirstevent = getEventID(firstevent);
                projectDispatch({ type: 'SetSelectedEvent', eventID: idfirstevent, eventType: firstevent.elType });
            } else {
                const eventFromList = getEventFromList(selectedEventID, selectedEventTypeName);
                setSelectedEventObject(eventFromList);
            }
        }
    }, [timelineEvents, selectedEventID, selectedEventTypeName, projectDispatch]);

    const getEventFromList = (eventId, eventType) => {
        const ev = timelineEvents.find((item) => {
            if (item.elType === eventType) {
                if (item.ID) {
                    return item.ID === eventId;
                } else {
                    return item.GroupedEventIDs.indexOf(eventId) >= 0;
                }
            } else {
                return false;
            }
        });
        return ev;
    };

    const handleMoveToEvent = (eventId, eventType) => {
        if (eventType.toLowerCase() === 'package') {
            setPackagePreviewID(eventId);
            return;
        }

        projectDispatch({ type: 'SetSelectedEvent', eventID: eventId, eventType });
    };

    const timelineEventRef = useRef();

    useEffect(() => {
        if (timelineEventRef && timelineEventRef.current) {
            timelineEventRef.current.scrollIntoView({
                behavior: 'smooth',
                inline: 'center',
                block: 'nearest',
            });
        }
    }, [timelineEventRef.current, selectedEventObject]);

    const isEventSelected = (projectEvent) => {
        if (selectedEventObject) {
            if (projectEvent.TypeName === selectedEventObject.TypeName) {
                if (projectEvent.GroupGuid && selectedEventObject.GroupGuid) {
                    return projectEvent.GroupGuid === selectedEventObject.GroupGuid;
                }
                if (projectEvent.GroupedEventIDs && selectedEventObject.GroupedEventIDs) {
                    return _.isEqual(projectEvent.GroupedEventIDs, selectedEventObject.GroupedEventIDs);
                }
                if (projectEvent.ID && selectedEventObject.ID) {
                    return projectEvent.ID === selectedEventObject.ID;
                }
            }
        }
        return false;
    };

    const getEventKey = (event) => {
        return event.uniqID;
    };

    return (
        <>
            <TimeLineContainer>
                <ProjectEventsTimelineContainer>
                    {isFetching ? (
                        <Loading />
                    ) : (
                        timelineEvents &&
                        timelineEvents.length > 0 &&
                        timelineEvents.map((timelineEvent) => (
                            <div
                                ref={isEventSelected(timelineEvent) ? timelineEventRef : null}
                                key={getEventKey(timelineEvent)}
                            >
                                <ProjectTimelineEvent
                                    isSelected={isEventSelected(timelineEvent)}
                                    event={timelineEvent}
                                    onSelectEvent={handleMoveToEvent}
                                />
                            </div>
                        ))
                    )}
                </ProjectEventsTimelineContainer>
            </TimeLineContainer>
            {selectedEventObject && (
                <EventCard
                    projectId={projectId}
                    currentProject={currentProject}
                    event={selectedEventObject}
                    modelId={modelId}
                    onSetLayout={() => {}}
                />
            )}
            {packagePreviewID && (
                <PackageDrawer packageId={packagePreviewID} onClose={() => setPackagePreviewID(null)} />
            )}
        </>
    );
};

export default ProjectEvents;
